import type { CompetitionId, RobotValues, SimResult } from '../types';

function num(v: number | string | undefined, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Rule-based Taa Bot coach: turns a simulation result + robot config
 * into targeted, physics-grounded suggestions (like a human mentor would).
 */
export function analyze(result: SimResult, robot: RobotValues): string[] {
  const tips: string[] = [];
  const byId: Record<CompetitionId, () => void> = {
    sumo: () => {
      const reaction = num(robot.reactionMs, 120);
      const speed = num(robot.maxSpeed, 0.7);
      const torque = num(robot.torqueNm, 0.5);
      const grip = num(robot.gripMu, 0.8);
      const weight = num(robot.weightKg, 1.8);
      const traction = grip * weight * 9.81;
      const driveForce = (torque * 2) / 0.04;

      if (result.outcome.includes('left the ring')) {
        if (reaction > 100)
          tips.push(`Your edge sensor detected the border, but your reverse action was delayed (${reaction} ms). Reduce the reaction delay below ~100 ms.`);
        if (speed > 0.8)
          tips.push(`At ${speed.toFixed(2)} m/s your braking distance is ~${(speed * reaction) / 10}cm per ${reaction}ms of delay — too fast for the edge margin. Lower top speed or react faster.`);
        if (tips.length === 0)
          tips.push('You drove out during a push contest. Try the Defender strategy to hold the center, where the edge is far away.');
      }
      if (driveForce > traction * 1.1)
        tips.push(`Your motors produce ${driveForce.toFixed(0)} N but traction only allows ${traction.toFixed(0)} N — wheels slip and waste power. Increase wheel grip or weight, or reduce torque.`);
      else if (!result.won && traction < 16)
        tips.push(`Pushing force is weak (${traction.toFixed(0)} N traction). Increase grip (soft silicone wheels) or add weight low in the chassis.`);
      if (!result.won && num(robot.sensorRange, 0.8) < 0.6)
        tips.push('Your opponent sensor range is short — you spend too long searching. Try 0.8–1.0 m range or a wider cone.');
      if (result.won) {
        tips.push('Strong round! To make it consistent, test against the Tank preset: can you still push a 2.9 kg robot? If not, raise grip before torque.');
      }
    },
    line: () => {
      const losses = num(result.raw?.losses, 0);
      const osc = num(result.raw?.oscPerSec, 0);
      const speed = num(robot.baseSpeed, 0.6);
      const kd = num(robot.kd, 1.5);
      const slowdown = num(robot.curveSlowdown, 0.4);

      if (losses > 0 && speed > 0.7)
        tips.push(`Your robot is too fast before sharp turns (${losses} line losses at ${speed.toFixed(2)} m/s base speed). Try reducing speed by 20% or raising curve slowdown above ${Math.min(slowdown + 0.15, 0.8).toFixed(2)}.`);
      else if (losses > 0)
        tips.push(`You lost the line ${losses}×. Raise Kp slightly so the robot corrects earlier, and check curve slowdown.`);
      if (osc > 0.8)
        tips.push(`Your PID is oscillating (${osc.toFixed(1)} flips/s). Increase Kd to ${(kd + 1).toFixed(1)} to damp the shaking, or lower Kp by ~25%.`);
      if (String(robot.sensors) === '3' && losses > 1)
        tips.push('A 3-sensor array gives a coarse error signal. Upgrade to 5 or 8 sensors for smoother steering at speed.');
      if (result.won && losses === 0 && osc < 0.6)
        tips.push(`Clean run in ${result.metrics['Time'] ?? '?'} — now push base speed up by 0.1 m/s and re-tune. Champions iterate speed in small steps.`);
    },
    maze: () => {
      const collisions = num(result.raw?.collisions, 0);
      const efficiency = num(result.raw?.efficiency, 0);
      const cal = num(robot.turnCalibration, 0.8);

      if (collisions > 0)
        tips.push(`Your robot is hitting walls because the turn angle is not calibrated (${cal.toFixed(2)}). Raise turn calibration — on a real robot, add wheel encoders or an IMU for exact 90° turns.`);
      if (result.won && efficiency < 0.75 && !String(robot.algorithm ?? '').startsWith('Flood'))
        tips.push(`Your maze strategy works, but the path is not optimal (${Math.round(efficiency * 100)}% efficiency). Switch to Flood fill in Known Maze mode for the shortest route.`);
      if (String(robot.sensorNoise ?? '').startsWith('Ultrasonic'))
        tips.push('Ultrasonic echoes are noisy in tight corridors. A ToF laser sensor reduces misreads and collision risk.');
      if (!result.won && result.outcome.includes('Time'))
        tips.push('Out of time — raise drive speed, or use flood fill to avoid exploring dead ends.');
      if (result.won && efficiency >= 0.95)
        tips.push('Near-optimal path! Next challenge: raise speed and keep calibration high — that is the real micromouse trade-off.');
    },
    battle: () => {
      const weight = num(robot.weightKg, 2);
      const speed = num(robot.maxSpeed, 0.8);
      const range = num(robot.sensorRange, 1);
      const momentum = weight * speed;

      if (!result.won && momentum < 1.4)
        tips.push(`Your hit momentum is low (${momentum.toFixed(1)} kg·m/s). Momentum = mass × speed — add weight or speed to win collisions.`);
      if (!result.won && weight > 2.5 && speed < 0.7)
        tips.push('Your robot is powerful but too slow — the opponent scores zone points while you chase. Drop ~0.5 kg or raise top speed.');
      if (range < 1.0)
        tips.push(`Sensor range ${range.toFixed(1)} m is shorter than the arena half-width — you get hit from blind spots. Extend it to 1.2 m+.`);
      if (String(robot.strategy) === 'Aggressive' && !result.won)
        tips.push('Aggressive charging misses cost energy. Try Balanced: attack only when the opponent is detected close.');
      if (result.won)
        tips.push('Victory! Try the Bruiser preset opponent scenario next: hold the center zone and make the heavy robot come to you.');
    },
  };
  byId[result.competition]();
  if (tips.length === 0) tips.push('Solid run. Change one variable at a time and re-simulate — that is how real teams tune robots.');
  return tips;
}

/** One-line verdict for the report header. */
export function verdict(result: SimResult): string {
  if (result.won) return 'Mission accomplished — now make it repeatable.';
  return 'Every loss is data. Check the suggestions below and re-run.';
}
